import { ApproveRejectReportDto, HarvestActivityHourSyncDto, HarvestActivityProductiveActivitySyncDto, HarvestActivityReportActivityHourDto, HarvestActivityReportDto, HarvestActivityReportProductiveActivityDto, HarvestActivitySyncDto } from "@proxy/dtos";
import { CreateHarvestActivityReportActivityHourInputDto, CreateHarvestActivityReportProductiveActivityInputDto } from "@proxy/integrations/harvest-activity-reports";
import { ActivityHour, HarvestActivityReport, HarvestActivityReportStatus, ProductiveActivity } from "../local-db/objects/harvest-activity-report";

export class ReportMapperHelper {
  static mapReportToApproveRejectDto(report: HarvestActivityReport, userName: string, isApproved: boolean, comment: string = ''): ApproveRejectReportDto {
    return {
      activityReportId: report.id,
      rejectionComment: comment,
      authorizationBy: userName,
      isApproved,
      operatorName: report.operatorName,
      number: report.number,
      date: report.date.toLocalISOString(),
      shiftId: report.shiftId,
      machineId: report.machineId,
      teamId: report.teamId,
      machineStartHourMeter: report.machineStartHourMeter,
      machineEndHourMeter: report.machineEndHourMeter,
      totalProductiveHours: report.totalProductiveHours,
      totalUnproductiveHours: report.totalUnproductiveHours,
      totalMaintenanceHours: report.totalMaintenanceHours,
      utilizationPercentage: report.utilizationPercentage,
      mechanicalAvailabilityPercentage: report.mechanicalAvailabilityPercentage,
      operationalAvailabilityPercentage: report.operationalAvailabilityPercentage,
      productiveActivities: this.mapProductiveActivities(report.productiveActivities),
      unproductiveHours: this.mapHours(report.unproductiveHours),
      isAuthorizable: report.isAuthorizable,
      approvalRestrictionMessage: report.approvalRestrictionMessage
    };
  }

  static mapHarvestActivityReportDtoToHarvestActivityReport(dto: HarvestActivityReportDto): HarvestActivityReport {
    return {
      id: dto.harvestActivityReportId,
      number: dto.number,
      operatorName: dto.operatorName,
      status: dto.isAuthorized ? HarvestActivityReportStatus.Approved : HarvestActivityReportStatus.Rejected,
      date: new Date(dto.date),
      shiftId: dto.shiftExternalId,
      machineId: dto.machineExternalId,
      teamId: dto.teamExternalId,
      machineStartHourMeter: dto.machineStartHourMeter,
      machineEndHourMeter: dto.machineEndHourMeter,
      totalProductiveHours: dto.totalProductiveHours,
      totalUnproductiveHours: dto.totalUnproductiveHours,
      totalMaintenanceHours: dto.totalMaintenanceHours,
      utilizationPercentage: dto.utilizationPercentage,
      mechanicalAvailabilityPercentage: dto.mechanicalAvailabilityPercentage,
      operationalAvailabilityPercentage: dto.operationalAvailabilityPercentage,
      productiveActivities: this.mapDtoToProductiveActivities(dto.productiveActivities),
      unproductiveHours: this.mapDtoToActivityHours(dto.unproductiveHours),
      isAuthorizable: dto.isAuthorizable,
      approvalRestrictionMessage: dto.approvalRestrictionMessage
    };
  }

  static mapSyncDtoToHarvestActivityReport(dto: HarvestActivitySyncDto): HarvestActivityReport {
    return {
      id: dto.id,
      number: dto.number,
      date: new Date(dto.date),
      status: HarvestActivityReportStatus.Pending,
      machineId: dto.machineId,
      teamId: dto.teamId,
      shiftId: dto.shiftId,
      machineStartHourMeter: dto.machineStartHourMeter,
      machineEndHourMeter: dto.machineEndHourMeter,
      operatorName: dto.operatorName,
      totalProductiveHours: dto.totalProductiveHours,
      totalUnproductiveHours: dto.totalUnproductiveHours,
      totalMaintenanceHours: dto.totalMaintenanceHours,
      utilizationPercentage: dto.utilizationPercentage,
      mechanicalAvailabilityPercentage: dto.mechanicalAvailabilityPercentage,
      operationalAvailabilityPercentage: dto.operationalAvailabilityPercentage,
      productiveActivities: dto.productiveActivities?.map(activity => this.mapProductiveActivity(activity)) || [],
      unproductiveHours: dto.unproductiveHours?.map(hour => this.mapActivityHour(hour)) || [],
      isAuthorizable: dto.isAuthorizable,
      approvalRestrictionMessage: dto.approvalRestrictionMessage,
    };
  }

  private static mapDtoToProductiveActivities(dtoActivities: HarvestActivityReportProductiveActivityDto[]): ProductiveActivity[] {
    return dtoActivities.map(dto => ({
      standName: dto.standName,
      length: dto.length,
      treeCount: dto.treeCount,
      loadCount: dto.loadCount,
      observations: dto.observations,
      spicesName: dto.speciesName,
      volumePerTree: dto.volumePerTree,
      volume: dto.volume,
      m3Effective: dto.m3Effective,
      treeEffective: dto.treeEffective,
      machineCapacity: dto.machineCapacity,
      distance: dto.distance,
      cartsEffectiveHours: dto.cartsEffectiveHours,
      middleDistance: dto.middleDistance,
      productiveHours: this.mapDtoToActivityHours(dto.productiveHours),
    }));
  }

  private static mapDtoToActivityHours(dtoHours: HarvestActivityReportActivityHourDto[]): ActivityHour[] {
    return dtoHours.map(dto => ({
      totalHours: dto.totalHour,
      observations: dto.observations,
      productivityName: dto.productivityName,
      hourTypeName: dto.hourTypeName,
      productivityTypeCode: dto.productivityTypeCode,
      localName: dto.localName,
      startTime: new Date(dto.startTime),
      endTime: new Date(dto.endTime),
    }));
  }

  private static mapProductiveActivities(activities: ProductiveActivity[]): CreateHarvestActivityReportProductiveActivityInputDto[] {
    return activities.map(activity => ({
      standName: activity.standName,
      length: activity.length,
      treeCount: activity.treeCount,
      loadCount: activity.loadCount,
      observations: activity.observations,
      speciesName: activity.spicesName,
      volumePerTree: activity.volumePerTree,
      volume: activity.volume,
      m3Effective: activity.m3Effective,
      treeEffective: activity.treeEffective,
      machineCapacity: activity.machineCapacity,
      distance: activity.distance,
      cartsEffectiveHours: activity.cartsEffectiveHours,
      middleDistance: activity.middleDistance,
      productiveHours: this.mapHours(activity.productiveHours),
    }));
  }

  private static mapHours(hours: ActivityHour[]): CreateHarvestActivityReportActivityHourInputDto[] {
    return hours.map(hour => ({
      totalHour: hour.totalHours,
      observations: hour.observations,
      productivityName: hour.productivityName,
      hourTypeName: hour.hourTypeName,
      productivityTypeCode: hour.productivityTypeCode,
      localName: hour.localName,
      startTime: hour.startTime.toLocalISOString(),
      endTime: hour.endTime.toLocalISOString(),
    }));
  }

  private static mapActivityHour(hour: HarvestActivityHourSyncDto): ActivityHour {
    return {
      totalHours: hour.totalHours || 0,
      observations: hour.observations || '',
      productivityName: hour.productivityName || '',
      hourTypeName: hour.hourTypeName || '',
      productivityTypeCode: hour.productivityTypeCode || '',
      localName: hour.localName || '',
      startTime: hour.startTime ? new Date(hour.startTime) : new Date(),
      endTime: hour.endTime ? new Date(hour.endTime) : new Date(),
    };
  }

  private static mapProductiveActivity(activity: HarvestActivityProductiveActivitySyncDto): ProductiveActivity {
    return {
      standName: activity.standName || '',
      observations: activity.observations || '',
      length: activity.length || 0,
      treeCount: activity.treeCount || 0,
      loadCount: activity.loadCount || 0,
      spicesName: activity.speciesName || '',
      volumePerTree: activity.volumePerTree || 0,
      volume: activity.volume || 0,
      m3Effective: activity.m3Effective || 0,
      treeEffective: activity.treeEffective || 0,
      machineCapacity: activity.machineCapacity || 0,
      distance: activity.distance || 0,
      cartsEffectiveHours: activity.cartsEffectiveHours || 0,
      middleDistance: activity.middleDistance || 0,
      productiveHours: activity.productiveHours?.map(hour => this.mapActivityHour(hour)) || [],
    };
  }
}